import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Mail, 
  Phone, 
  Calendar, 
  Users, 
  TrendingUp, 
  FileText, 
  DollarSign,
  Award,
  QrCode,
  MapPin,
  CheckCircle2
} from 'lucide-react';
import { VendedorDetailedMetrics } from '@/types/vendedores';
import { MetricsGrid } from '@/components/dashboard/MetricsGrid';

// Função de formatação de telefone
const formatTelefone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  } else if (digits.length === 10) {
    return digits.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
};

interface VendedorOverviewTabProps {
  vendedor: VendedorDetailedMetrics;
}

export function VendedorOverviewTab({ vendedor }: VendedorOverviewTabProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Dados Pessoais */}
      <Card>
        <CardHeader className="px-4 sm:px-6">
          <CardTitle className="text-base sm:text-lg">Informações do Vendedor</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 px-4 sm:px-6 pb-4 sm:pb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1 min-w-0">
              <div className="flex items-center text-sm text-muted-foreground">
                <Users className="h-4 w-4 mr-2 shrink-0" />
                Nome
              </div>
              <p className="font-medium break-words">{vendedor.vendedor_nome}</p>
            </div>
            
            <div className="space-y-1 min-w-0">
              <div className="flex items-center text-sm text-muted-foreground">
                <Mail className="h-4 w-4 mr-2 shrink-0" />
                Email
              </div>
              <p className="font-medium break-all">{vendedor.vendedor_email || 'Não informado'}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center text-sm text-muted-foreground">
                <Phone className="h-4 w-4 mr-2" />
                Telefone
              </div>
              <p className="font-medium">{vendedor.vendedor_telefone ? formatTelefone(vendedor.vendedor_telefone) : 'Não informado'}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 mr-2" />
                Localização
              </div>
              <p className="font-medium">
                {vendedor.vendedor_cidade && vendedor.vendedor_estado 
                  ? `${vendedor.vendedor_cidade}/${vendedor.vendedor_estado}`
                  : 'Não informado'}
              </p>
            </div>
            
            {vendedor.vendedor_pix && (
              <div className="space-y-1">
                <div className="flex items-center text-sm text-muted-foreground">
                  <QrCode className="h-4 w-4 mr-2" />
                  Chave Pix
                </div>
                <p className="font-medium">{vendedor.vendedor_pix}</p>
              </div>
            )}
            
            <div className="space-y-1">
              <div className="flex items-center text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 mr-2" />
                Data de Início
              </div>
              <p className="font-medium">{formatDate(vendedor.data_inicio)}</p>
            </div>
            
            <div className="space-y-1">
              <div className="flex items-center text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Status
              </div>
              <Badge variant={vendedor.status ? 'default' : 'secondary'}>
                {vendedor.status ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dashboard de Métricas do Vendedor */}
      <div className="space-y-4">
        <h3 className="text-base sm:text-lg font-semibold text-foreground">Dashboard do Vendedor</h3>
        <MetricsGrid 
          metrics={{
            totalPlacas: vendedor.total_placas,
            potenciaInstalada: vendedor.potencia_instalada,
            contratosFechados: vendedor.contratos_fechados,
            receitaTotal: vendedor.valor_contratos,
            lucroPorPlaca: vendedor.lucro_por_placa,
            taxaConversao: vendedor.taxa_conversao_leads_contratos,
            leadsCapturados: vendedor.total_leads,
            totalParceiros: vendedor.total_parceiros,
            totalPropostas: vendedor.propostas_enviadas,
            valorTotalPropostas: vendedor.valor_propostas
          }}
          exclude={["Vendedores Cadastrados"]}
        />
      </div>

      {/* Melhor Parceiro */}
      {vendedor.melhor_parceiro_nome && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-600" />
              Parceiro Destaque
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-semibold text-base sm:text-lg break-words">{vendedor.melhor_parceiro_nome}</p>
                <p className="text-sm text-muted-foreground">
                  Melhor performance da rede
                </p>
              </div>
              <div className="text-left sm:text-right shrink-0">
                <p className="text-xl sm:text-2xl font-bold text-yellow-600">
                  {vendedor.melhor_parceiro_contratos}
                </p>
                <p className="text-xs text-muted-foreground">contratos fechados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

